import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ApiService } from '@services/api/api.service';
import { ReduxService } from '@state-management/redux.service';
import { AiActionCardState, AiConfirmationDropdown, AiConfirmValues, AiPendingAction } from '../../models/ai-assistant.models';
import { friendlyDay } from '../../utils/ai-date.util';

interface ArgRow {
  label: string;
  value: string;
}

/** Arg keys that may carry the closing reason for a no-further-interest close. */
const REASON_ARG_KEYS = ['reason', 'reason_to_closed', 'close_reason', 'closing_reason', 'reason_for_close', 'not_interested_reason'];

/**
 * Fields the frontend can turn into an inline dropdown by loading the office's lead tags — used
 * when the backend didn't already supply a `confirmation.dropdowns` entry for them (e.g. the
 * status on an update, or the closing reason). Options come from the same source the Leads form
 * uses (`redux.getTags(..., 'lead') → data[0].types[]`), so they always match the CRM.
 */
const TAG_DROPDOWNS: { keys: string[]; field: string; slug: string; label: string }[] = [
  { keys: ['lead_status', 'status'], field: 'lead_status', slug: 'lead-status', label: 'Lead Status' },
  { keys: REASON_ARG_KEYS, field: 'reason', slug: 'lead-status-no-further-interest', label: 'Reason' },
];

/**
 * Legacy fallback only — used when the backend response omits `pending_action.confirmation`.
 * Internal identifiers must never surface: anything named `id`/`_id` or ending in `_id`.
 */
const HIDDEN_ARG_KEYS = new Set(['id', '_id', 'action_id', 'conversation_id', 'office_id', 'user_id']);

/** Legacy fallback: friendly labels for known argument keys. */
const ARG_LABELS: Record<string, string> = {
  lead_status: 'Status',
  lead_type: 'Type',
  lead_source: 'Source',
  follow_up_date: 'Follow-up date',
  due_date: 'Date',
  task_time: 'Time',
  task_date: 'Date',
  assigned_agent: 'Agent',
  reason: 'Reason',
  reason_to_closed: 'Reason',
  close_reason: 'Reason',
  closing_reason: 'Reason',
  reason_for_close: 'Reason',
};

/** Legacy fallback: argument keys whose value is a date and should be shown in friendly form. */
const DATE_ARG_KEYS = new Set(['follow_up_date', 'due_date', 'task_date', 'date', 'appointment_date']);

/**
 * Confirm / cancel card for a write action the assistant proposed (`pending_action`).
 * Presentational — the parent wires {@link confirm} / {@link cancel} to the state service.
 * The assistant never auto-executes; nothing happens until the user confirms here.
 *
 * Renders the backend-built `pending_action.confirmation` (heading + friendly label/value
 * rows). It never displays `args` (a sanitized machine payload), `lead_id`, or any raw key.
 */
@Component({
  selector: 'app-ai-assistant-action-card',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './ai-assistant-action-card.component.html',
  styleUrls: ['./ai-assistant-action-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantActionCardComponent implements OnInit {
  @Input({ required: true }) action!: AiPendingAction;
  @Input() state: AiActionCardState = 'pending';
  /** Writes still queued after this one (multi-action requests); 0 hides the indicator. */
  @Input() queuedRemaining = 0;

  /** Emits the user's inline field picks (dropdowns + closing reason) when they confirm. */
  @Output() confirm = new EventEmitter<AiConfirmValues>();
  @Output() cancel = new EventEmitter<void>();

  /** Backend-supplied editable dropdowns (create_lead type/source/status, …). */
  dropdowns: AiConfirmationDropdown[] = [];
  /** Frontend-built dropdowns (status / closing reason) loaded from the office's lead tags. */
  tagDropdowns: AiConfirmationDropdown[] = [];
  /** Current selection per dropdown field, keyed by `dropdown.field`. */
  selected: Record<string, string> = {};
  /** Current value per tag-dropdown field — used to drop the now-redundant static row. */
  private supplementedValues: Record<string, string> = {};

  constructor(
    private readonly redux: ReduxService,
    private readonly api: ApiService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initDropdowns();
    this.initTagDropdowns();
  }

  /** All dropdowns shown on the card — backend-provided first, then frontend tag-built. */
  get editableDropdowns(): AiConfirmationDropdown[] {
    return this.tagDropdowns.length ? [...this.dropdowns, ...this.tagDropdowns] : this.dropdowns;
  }

  /** Seed each backend dropdown with its prefilled value so confirm always sends a full set. */
  private initDropdowns(): void {
    this.dropdowns = this.action?.confirmation?.dropdowns || [];
    for (const dd of this.dropdowns) {
      this.selected[dd.field] = dd.value || '';
    }
  }

  /**
   * Fallback dropdowns for status / reason fields, loaded from the office's lead tags. Used ONLY
   * when the backend sent no `confirmation.dropdowns` — it's now authoritative about which fields
   * are editable (create_lead type/source/status, close_lead reason, …), so when it provides any
   * we render exactly those and add nothing. Non-fatal — if no options resolve, the static row stays.
   */
  private initTagDropdowns(): void {
    if (this.dropdowns.length) {
      return; // backend drives the editable fields — don't supplement
    }
    const args = this.action?.args || {};
    const wanted = TAG_DROPDOWNS.map(cfg => {
      const key = cfg.keys.find(k => k in args);
      if (!key) {
        return null;
      }
      return { cfg, current: `${args[key] ?? ''}`.trim() };
    }).filter((x): x is { cfg: (typeof TAG_DROPDOWNS)[number]; current: string } => x !== null);

    if (!wanted.length) {
      return;
    }

    this.redux.getTags(this.api.getOfficeId(), false, 'lead').subscribe({
      next: res => {
        const types: any[] = (res as any)?.data?.[0]?.types || [];
        for (const { cfg, current } of wanted) {
          const type = types.find(t => (t?.slug || '').toLowerCase() === cfg.slug);
          const options: string[] = ((type?.data as any[]) || [])
            .map(d => `${d?.tag_name ?? ''}`.trim())
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b));
          if (!options.length) {
            continue; // no options configured — keep the static row for this field
          }
          // Keep the current value selectable even if it isn't in the office's list.
          if (current && !options.includes(current)) {
            options.unshift(current);
          }
          this.tagDropdowns.push({ field: cfg.field, label: cfg.label, required: false, value: current, options });
          this.selected[cfg.field] = current;
          this.supplementedValues[cfg.field] = current;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        /* keep the static rows */
      },
    });
  }

  /** Every required dropdown must have a value before the user can confirm. */
  get canConfirm(): boolean {
    return this.editableDropdowns.every(dd => !dd.required || `${this.selected[dd.field] || ''}`.trim() !== '');
  }

  /** Confirm the action, attaching every dropdown pick (backend + tag-built) as `values`. */
  onConfirm(): void {
    if (!this.canConfirm) {
      return;
    }
    const values: AiConfirmValues = {};
    for (const dd of this.editableDropdowns) {
      const v = `${this.selected[dd.field] || ''}`.trim();
      if (v) {
        values[dd.field] = v;
      }
    }
    this.confirm.emit(values);
  }

  /** Card heading — the lead NAME from the confirmation payload (never an id). */
  get leadName(): string {
    return this.action?.confirmation?.lead_name || '';
  }

  /** Affected record names for a bulk confirmation (close/complete/move many); empty for single actions. */
  get bulkItemNames(): string[] {
    return (this.action?.confirmation?.items || []).map(it => (it?.name || '').trim()).filter(Boolean);
  }

  /**
   * Friendly label/value rows for the review preview. Renders the backend's
   * `confirmation.fields` verbatim; falls back to sanitized args only for legacy responses
   * that predate the `confirmation` payload.
   */
  get rows(): ArgRow[] {
    return this.hideSupplementedRows(this.buildRows());
  }

  private buildRows(): ArgRow[] {
    const fields = this.action?.confirmation?.fields;
    if (fields?.length) {
      return fields
        .filter(field => field && field.label && `${field.value ?? ''}`.trim() !== '')
        .map(field => ({ label: field.label, value: `${field.value}` }));
    }
    return this.legacyRowsFromArgs();
  }

  /** Drop static rows whose value a tag-built dropdown now replaces (each matched once). */
  private hideSupplementedRows(rows: ArgRow[]): ArgRow[] {
    const values = Object.values(this.supplementedValues).filter(Boolean);
    if (!values.length) {
      return rows;
    }
    const remaining = [...values];
    return rows.filter(row => {
      const i = remaining.indexOf(row.value);
      if (i !== -1) {
        remaining.splice(i, 1);
        return false;
      }
      return true;
    });
  }

  get isBusy(): boolean {
    return this.state === 'confirming' || this.state === 'cancelling';
  }

  get isResolved(): boolean {
    return (
      this.state === 'confirmed' || this.state === 'cancelled' || this.state === 'failed' || this.state === 'historical'
    );
  }

  // ---- Legacy fallback (pre-`confirmation` backends) --------------------------

  private legacyRowsFromArgs(): ArgRow[] {
    const args = this.action?.args || {};
    return Object.keys(args)
      .filter(key => !this.isHidden(key))
      .filter(key => args[key] !== null && args[key] !== undefined && `${args[key]}`.trim() !== '')
      .map(key => ({ label: this.humanize(key), value: this.format(key, args[key]) }));
  }

  /** Hide internal identifiers (`id`, `*_id`, known technical keys) from the user. */
  private isHidden(key: string): boolean {
    const k = key.toLowerCase();
    return HIDDEN_ARG_KEYS.has(k) || /_id$/.test(k);
  }

  private humanize(key: string): string {
    const mapped = ARG_LABELS[key.toLowerCase()];
    if (mapped) {
      return mapped;
    }
    const text = key.replace(/_/g, ' ').trim();
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  private format(key: string, value: unknown): string {
    if (Array.isArray(value)) {
      return value
        .map(v => this.scalar(v))
        .filter(Boolean)
        .join(', ');
    }
    if (DATE_ARG_KEYS.has(key.toLowerCase()) && typeof value === 'string') {
      return friendlyDay(value);
    }
    return this.scalar(value);
  }

  /** Collapse a value to a readable string, unwrapping common object shapes. */
  private scalar(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      return `${obj['name'] ?? obj['label'] ?? obj['value'] ?? ''}`;
    }
    return `${value}`;
  }
}
